// src/components/pages/message/MessagesPage.js
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Typography, Tabs, Tab, Box } from "@mui/material";
import {
  SwipeableList,
  SwipeableListItem,
  TrailingActions,
  SwipeAction,
} from "react-swipeable-list";
import "react-swipeable-list/dist/styles.css";
import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import { MESSAGE_TABS_POSITION, SCROLLABLE_MESSAGE_LIST_POSITION, SCROLLABLE_MESSAGE_LIST_HEIGHT_OFFSET } from "../../../constants/constants";

// Firebase
import { db, auth } from "../../../firebase";
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";

const MessagesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = parseInt(searchParams.get("tab") || "0", 10);
  const [tabValue, setTabValue] = useState(initialTab);
  const [receivedMessages, setReceivedMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);

  // Subscribe to Firestore
  useEffect(() => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;

    // Received: any capsule where I'm a recipient
    const receivedQ = query(
      collection(db, "timeCapsules"),
      where("recipientUserIds", "array-contains", uid),
      orderBy("sentAt", "desc")
    );
    const unsubReceived = onSnapshot(receivedQ, (snap) => {
      setReceivedMessages(
        snap.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            address:
              data.location?.address?.[0] ??
              data.address ??
              "Unknown address",
              subject: data.messageTitle ?? "",
              message: data.messageBody ?? "",
          };
        })
      );
    });

    // Sent: any capsule I sent
    const sentQ = query(
      collection(db, "timeCapsules"),
      where("senderId", "==", uid),
      orderBy("sentAt", "desc")
    );
    const unsubSent = onSnapshot(sentQ, (snap) => {
      setSentMessages(
        snap.docs.map((doc) => {
          const data = doc.data();
          console.log('sent message data: ', data);
          return {
            id: doc.id,
            address:
              data.location?.address ??
              data.address ??
              "Unknown address",
            subject: data.messageTitle ?? "",
            message: data.messageBody ?? "",
          };
        })
      );
    });

    return () => {
      unsubReceived();
      unsubSent();
    };
  }, []);

  const handleChangeTab = (_, newValue) => {
    setTabValue(newValue);
    // pushes /messages?tab=0 or ?tab=1
    setSearchParams({ tab: newValue });
  };


  async function archiveMessage(capsuleId, uid) {
    const capsuleRef = doc(db, "timeCapsules", capsuleId);
    await updateDoc(capsuleRef, {
      archivedBy: arrayUnion(uid)
    });
  }

  const trailingActions = (msg) => (
    <TrailingActions>
      <SwipeAction destructive onClick={() => archiveMessage(msg.id, auth.currentUser.uid)}>
        <Box sx={{
          backgroundColor: theme => theme.colors.green,
          color: "white",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2
        }}>
          Archive
        </Box>
      </SwipeAction>
    </TrailingActions>
  );

  const renderSwipeableItem = (msg, type) => (
    <SwipeableListItem
      key={msg.id}
      trailingActions={trailingActions(msg, type)}
      onClick={() => navigate(`/capsule/${msg.id}`)}
    >
      <Box
        sx={{
          width: "100%",
          backgroundColor: "#fff",
          p: 2,
          borderBottom: "1px solid #ccc",
          cursor: "pointer",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          {msg.subject}
        </Typography>
        <Typography variant="body2">{msg.message}</Typography>
        {/* <Typography variant="body2">{msg.address}</Typography> */}
      </Box>
    </SwipeableListItem>
  );

  return (
    <Box
      sx={{
        position: "relative",
        height: `calc(100vh - 390px)`,
        bgcolor: "white",
      }}
    >
      {/* Tabs */}
      <Box
        sx={{
          position: "fixed",
          top: `${MESSAGE_TABS_POSITION}px`,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10,
          width: "60vw",
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleChangeTab}
          variant="fullWidth"
          TabIndicatorProps={{ style: { display: "none" } }}
        >
          <Tab
            label="Received"
            sx={(theme) => ({
              textTransform: "none",
              borderTopLeftRadius: "50px",
              borderBottomLeftRadius: "50px",
              border: `1px solid ${theme.colors.blue}`,
              backgroundColor:
                tabValue === 0 ? theme.colors.blue : "#fff !important",
              color: tabValue === 0 ? "#fff !important" : theme.colors.blue,
              fontWeight: 700,
              flex: 1,
            })}
          />
          <Tab
            label="Sent"
            sx={(theme) => ({
              textTransform: "none",
              borderTopRightRadius: "50px",
              borderBottomRightRadius: "50px",
              border: `1px solid ${theme.colors.blue}`,
              backgroundColor:
                tabValue === 1 ? theme.colors.blue : "#fff !important",
              color: tabValue === 1 ? "#fff !important" : theme.colors.blue,
              fontWeight: 700,
              flex: 1,
            })}
          />
        </Tabs>
      </Box>

      {/* Message List */}
      <Box
        sx={{
          position: "absolute",
          top: `${SCROLLABLE_MESSAGE_LIST_POSITION}px`,
          height: `calc(100vh - ${SCROLLABLE_MESSAGE_LIST_HEIGHT_OFFSET}px)`,
          left: 0,
          right: 0,
          overflowY: "auto",
          backgroundColor: "white",
        }}
      >
        {tabValue === 0 && (
          <SwipeableList type="IOS" threshold={0.25}>
            {receivedMessages.map((msg) => renderSwipeableItem(msg, "received"))}
          </SwipeableList>
        )}
        {tabValue === 1 && (
          <SwipeableList type="IOS" threshold={0.25}>
            {sentMessages.map((msg) => renderSwipeableItem(msg, "sent"))}
          </SwipeableList>
        )}
      </Box>
    </Box>
  );
};

export default MessagesPage;